import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useDeletePromotionMutation,
  useGetPromotionsMutation,
} from "../../../../redux/reducer/features/Product/promotionSlice";
import AppConfirmDialog from "../../../app/AppConfirmDialog";
import {
  DataGridPro,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import PromotionDialog from "./PromotionDialog";
import PromotionProductsDialog from "./PromotionProductsDialog";
import dayjs from "dayjs";
import { AppState } from "../../../../redux/reducer/rootReducer";
import { useSelector } from "react-redux";

const PromotionsToolbar = () => (
  <GridToolbarContainer sx={{ bgcolor: "grey.50", py: 1.25, px: 0.5 }}>
    <Box display="flex" justifyContent="space-between" width="100%">
      <Box>
        <GridToolbarColumnsButton {...({} as any)} />
        <GridToolbarFilterButton {...({} as any)} />
        <GridToolbarDensitySelector {...({} as any)} />
        <GridToolbarExport />
      </Box>
      <Box>
        <GridToolbarQuickFilter />
      </Box>
    </Box>
  </GridToolbarContainer>
);

const PromotionsFooter = () => (
  <GridFooterContainer>
    <GridPagination {...({} as any)} />
  </GridFooterContainer>
);

interface Promotion {
  id: string;
  name: string;
  productType: string;
  category: string[];
  brand: string[];
  productNumber: string[];
  basePriceType: string;
  discountMethod: string;
  discountValue: number;
  startDate: string;
  endDate: string;
  status: string;
}

const FilterCell = ({ items }: { items: string[] | null }) => {
  if (!items || items.length === 0) return <span>All</span>;

  const preview = items.slice(0, 2).join(", ");
  const full = items.join(", ");

  return (
    <Tooltip title={full} arrow>
      <Typography
        variant="body2"
        noWrap
        sx={{ maxWidth: "100%", cursor: "default" }}
      >
        {preview}
        {items.length > 2 ? ` +${items.length - 2} more` : ""}
      </Typography>
    </Tooltip>
  );
};

const formatDiscount = (method: string, value: number): string => {
  if (method === "PercentDiscount") return `${value}% off`;
  return `$${value}`;
};

const Promotions: React.FunctionComponent = () => {
  const [openPromotionDialog, setOpenPromotionDialog] = React.useState(false);
  const [getPromotions, getPromotionsResult] = useGetPromotionsMutation();
  const [deletePromotion, deletePromotionResult] = useDeletePromotionMutation();
  const [promotions, setPromotions] = React.useState<Promotion[]>([]);
  const [viewPromotion, setViewPromotion] = React.useState<Promotion | null>(
    null,
  );
  const [deletePromotionId, setDeletePromotionId] = React.useState<
    string | null
  >(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = React.useState<Promotion | null>(null);
  const openMenu = Boolean(anchorEl);
  const dealerTz = useSelector(
    (state: AppState) =>
      state.authReducer.dmsSetup?.dealerTimeZoneIanaId ?? "UTC",
  );

  React.useEffect(() => {
    getPromotions();
  }, [getPromotions]);

  React.useEffect(() => {
    if (getPromotionsResult.isSuccess && getPromotionsResult.data) {
      setPromotions(getPromotionsResult.data.entityList ?? []);
    }
  }, [getPromotionsResult]);

  const handleCloseDialog = () => {
    setOpenPromotionDialog(false);
  };
  const handleDialogSuccess = () => {
    handleCloseDialog();
    getPromotions();
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 260,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value} arrow>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 600,
              cursor: "pointer",
              color: "primary.main",
              "&:hover": { textDecoration: "underline" },
            }}
            tabIndex={0}
            role="button"
            onClick={() => setViewPromotion(params.row)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setViewPromotion(params.row);
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "productType",
      headerName: "Product Type",
      minWidth: 130,
      flex: 1,
    },
    {
      field: "category",
      headerName: "Category",
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <FilterCell items={params.value} />
      ),
    },
    {
      field: "brand",
      headerName: "Brand",
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <FilterCell items={params.value} />
      ),
    },
    {
      field: "productNumber",
      headerName: "Product #",
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <FilterCell items={params.value} />
      ),
    },
    {
      field: "basePriceType",
      headerName: "Base Price",
      minWidth: 110,
      flex: 1,
    },
    {
      field: "discountValue",
      headerName: "Discount",
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        formatDiscount(params.row.discountMethod, params.row.discountValue),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.row.startDate
            ? dayjs.utc(params.row.startDate).tz(dealerTz).format("MM/DD/YYYY")
            : "N/A"}
        </Typography>
      ),
    },
    {
      field: "endDate",
      headerName: "End Date",
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.row.endDate
            ? dayjs.utc(params.row.endDate).tz(dealerTz).format("MM/DD/YYYY")
            : "N/A"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 110,
      flex: 1,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "Active"
              ? "success"
              : params.value === "Expired"
                ? "error"
                : "warning"
          }
        />
      ),
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 110,
      flex: 1,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.source === "DMS" ? (
          <Chip label="DMS" color="warning" size="small" />
        ) : (
          <Chip label="TargetWEB" color="success" size="small" />
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label="Action"
          color="info"
          size="small"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedRow(params.row);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Container maxWidth={false}>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={8}>
            <Grid item xs={4} key={1}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
                Price Promotions
              </Typography>
            </Grid>
            <Grid item xs={8} key={2}>
              <Grid container sx={{ justifyContent: "right" }} spacing={2}>
                <Grid item key={1}>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenPromotionDialog(true)}
                  >
                    Add Promotion
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Card>
          <CardContent sx={{ padding: 0, paddingBottom: "0px !important" }}>
            <Box sx={{ height: "70vh" }}>
              <DataGridPro
                rows={promotions}
                columns={columns}
                loading={getPromotionsResult.isLoading}
                disableSelectionOnClick
                pagination
                rowsPerPageOptions={[50, 100]}
                initialState={{
                  pagination: { pageSize: 50 },
                  columns: {
                    columnVisibilityModel: {
                      model: true,
                      units: true,
                      productNumber: true,
                    },
                  },
                }}
                components={{
                  Toolbar: PromotionsToolbar,
                  Pagination: PromotionsFooter,
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Container>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedRow) setViewPromotion(selectedRow);
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedRow) setDeletePromotionId(selectedRow.id);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      <PromotionDialog
        open={openPromotionDialog}
        onClose={handleCloseDialog}
        onSuccess={handleDialogSuccess}
      />
      <PromotionProductsDialog
        open={!!viewPromotion}
        onClose={() => setViewPromotion(null)}
        promotionId={viewPromotion?.id ?? null}
        promotionName={viewPromotion?.name ?? ""}
      />
      <AppConfirmDialog
        isOpen={!!deletePromotionId}
        handleClose={() => setDeletePromotionId(null)}
        headerText="Delete Promotion"
        bodyText="Are you sure you want to delete this promotion?"
        onSuccess={() => {
          if (deletePromotionId) {
            deletePromotion({ promotionId: deletePromotionId }).then(
              (res: any) => {
                if (res?.data?.status === 200 || res?.data?.status === 202) {
                  getPromotions();
                }
              },
            );
          }
          setDeletePromotionId(null);
        }}
        onFail={() => setDeletePromotionId(null)}
      />
    </>
  );
};

export default Promotions;
